import { TranslateService } from '@ngx-translate/core';
import { WidgetSimpleInfo } from '../campaign/widgets.model';
import { OnsiteCampaignType } from '../campaign/campaign.model';
import {
  ACTIONS_TYPES,
  ActionUpdateField,
  PROPS_TYPE,
  TriggerType,
  TriggerTypeArgument,
  UpdateRealTimeActions,
} from './automation-helpers-actions';
import { Account } from 'app/back-office/account.model';
import { AccountIdentifierConstants } from 'app/account/account-identifier-constants';
import { IntegrationType } from './integrations-models';
import {
  Automation,
  AutomationSourceType,
  CouponComponent,
  EventDefinition,
  TriggerCondition,
  TypeStep,
} from './automations-models';

export class AutomationHelpers {
  // oc_action_{widgetId}_{stateId} is a state action; a trailing _{componentId} makes it a component action.
  private static readonly componentActionGoalRegex = /^oc_action_(\d+)_\d+(?:_\d+)?$/;

  public static getComponentActionTrigger(automation: Automation): TriggerCondition | undefined {
    return (automation?.triggers ?? []).find((trigger) => AutomationHelpers.isComponentActionTrigger(trigger));
  }

  public static getComponentActionWidgetId(automation: Automation): number | undefined {
    return AutomationHelpers.getWidgetIdFromComponentActionTrigger(
      AutomationHelpers.getComponentActionTrigger(automation),
    );
  }

  public static isExperienceAutomation(automation: Automation): boolean {
    return !!(automation.isFormTrigger || automation.isComponentTrigger);
  }

  public static isAnyFormSubmission(automation: Automation): boolean {
    return (automation?.triggers ?? []).some(
      (trigger) => trigger.type === TriggerType.WidgetFormSubmission && !trigger.argument,
    );
  }

  public static getExperienceWidgetId(automation: Automation): number | undefined {
    const formTrigger = (automation?.triggers ?? []).find(
      (trigger) => trigger.type === TriggerType.WidgetFormSubmission,
    );
    if (formTrigger) {
      return +formTrigger.argument;
    }
    return (
      AutomationHelpers.getComponentActionWidgetId(automation) ??
      AutomationHelpers.getCouponComponentTrigger(automation)?.widgetId
    );
  }

  /** A coupon trigger is bound to its component through typed fields rather than a goal name. */
  public static isCouponComponentTrigger(trigger: TriggerCondition): boolean {
    return trigger?.type === TriggerType.Event && trigger.widgetId != null;
  }

  public static getCouponComponentTrigger(automation: Automation): TriggerCondition | undefined {
    return (automation?.triggers ?? []).find((trigger) => AutomationHelpers.isCouponComponentTrigger(trigger));
  }

  /**
   * Whether the automation belongs to a campaign component rather than standing on its own - it lists under
   * the campaign automations and takes its name from the component instead of a typed one. Deliberately
   * broader than {@link isComponentActionTrigger}, which stays about the click-goal shape alone.
   */
  public static isComponentActionAutomation(automation: Automation): boolean {
    return (
      AutomationHelpers.getComponentActionTrigger(automation) !== undefined ||
      AutomationHelpers.getCouponComponentTrigger(automation) !== undefined
    );
  }

  /**
   * Whether the trigger is bound to a campaign component, so repointing it would orphan that component's
   * reference to this automation. Separate from {@link isComponentActionTrigger}: that one describes the
   * click-goal shape, and the label and summary paths still need to tell the two apart.
   */
  public static isCampaignBoundTrigger(trigger: TriggerCondition): boolean {
    return AutomationHelpers.isComponentActionTrigger(trigger) || AutomationHelpers.isCouponComponentTrigger(trigger);
  }

  // Event triggers label themselves by subtype; every other type still labels by type
  public static getTriggerTitleKey(trigger: TriggerCondition): string {
    if (trigger?.subType) {
      return `automations.triggers.${trigger.subType}.title`;
    }
    return `automations.triggers.${trigger?.type}${trigger?.type === 'WidgetFormSubmission' ? '.titleCampaign' : '.title'}`;
  }

  // Names the trigger itself, for the dialog heading and the field label where a sentence would not read.
  public static getTriggerHeadingKey(trigger: TriggerCondition): string {
    return `${AutomationHelpers.triggerLabelKeyPrefix(trigger)}.title`;
  }

  // Label for an entry in the trigger picker. These read as sentences continuing "When a visitor:",
  // so they are their own key rather than the plain trigger name.
  public static getTriggerPickerTitleKey(trigger: TriggerCondition): string {
    return `${AutomationHelpers.triggerLabelKeyPrefix(trigger)}.pickerTitle`;
  }

  // Subtyped triggers label by subtype so they stay distinguishable; cart abandonment by its type argument.
  private static triggerLabelKeyPrefix(trigger: TriggerCondition): string {
    if (trigger?.typeArgument === TriggerTypeArgument.AbandonCart) {
      return `automations.triggers.${trigger.typeArgument}`;
    }
    if (trigger?.subType) {
      return `automations.triggers.${trigger.subType}`;
    }
    return `automations.triggers.${trigger?.type}`;
  }

  // Campaign name for a widget id, falling back to a readable placeholder when the campaign is gone
  public static getEventCampaignLabel(
    widgetId: number,
    widgets: WidgetSimpleInfo[],
    translateService: TranslateService,
  ): string {
    const widget = (widgets ?? []).find((w) => w.id === widgetId);
    if (widget) {
      return AutomationHelpers.getDynamicWidgetName(widget, translateService);
    }
    return translateService.instant('automations.triggers.Event.removedCampaign', { widgetId: widgetId });
  }

  // The picker offers one event-definition entry, so an option has to carry the subtype and the scope.
  // This encoding exists only between the dropdown and the trigger - what gets persisted is the typed
  // subType / widgetId / componentId fields, never a packed string.
  private static readonly eventOptionSeparator = '|';

  public static buildEventOptionValue(subType: string, widgetId: number | null, componentId: number | null): string {
    return [subType, widgetId ?? '', componentId ?? ''].join(AutomationHelpers.eventOptionSeparator);
  }

  public static parseEventOptionValue(value: string): {
    subType?: string;
    widgetId?: number;
    componentId?: number;
  } {
    const parts = (value ?? '').split(AutomationHelpers.eventOptionSeparator);
    if (parts.length < 2) {
      return {};
    }
    return {
      subType: parts[0] || undefined,
      widgetId: parts[1] ? +parts[1] : undefined,
      componentId: parts[2] ? +parts[2] : undefined,
    };
  }

  // What the dropdown should show as selected for an already-saved trigger. A trigger picked but not yet
  // scoped still packs its subtype, which is where the editor reads the chosen type from.
  public static toEventOptionValue(trigger: TriggerCondition): string {
    if (!trigger?.subType) {
      return null;
    }
    return AutomationHelpers.buildEventOptionValue(
      trigger.subType,
      trigger.widgetId ?? null,
      trigger.componentId ?? null,
    );
  }

  // Reached when the coupon can no longer be identified, which means the component was deleted -
  // the catalogue keeps the definition because the events it recorded still happened.
  public static getEventDefinitionLabel(
    event: EventDefinition,
    widgets: WidgetSimpleInfo[],
    translateService: TranslateService,
  ): string {
    const campaign = AutomationHelpers.getEventCampaignLabel(event.widgetId, widgets, translateService);
    if (event.componentId == null) {
      return campaign;
    }
    const removed = translateService.instant('automations.triggers.Event.removedComponent', {
      componentId: event.componentId,
    });
    return `${campaign} - ${removed}`;
  }

  // The subtype comes from the radio group, so a dropdown row identifies only the scope.

  // A unique coupon is named by its set, a static one by the code it renders. Both consumers - the
  // trigger picker and the events settings list - must agree, so the rule lives here.
  public static describeCoupon(coupon: CouponComponent, setNames: Map<string, string>): string {
    if (!coupon) {
      return '';
    }
    if (coupon.couponSetId) {
      return setNames?.get(coupon.couponSetId) ?? '';
    }
    return (coupon.code ?? '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/\s+/g, ' ')
      .trim();
  }

  public static buildEventScopeValue(widgetId: number, componentId: number | null): string {
    return [widgetId, componentId ?? ''].join(AutomationHelpers.eventOptionSeparator);
  }

  public static parseEventScopeValue(value: string): { widgetId?: number; componentId?: number } {
    const parts = (value ?? '').split(AutomationHelpers.eventOptionSeparator);
    return {
      widgetId: parts[0] ? +parts[0] : undefined,
      componentId: parts[1] ? +parts[1] : undefined,
    };
  }

  // The coupon a saved Event trigger points at. Callers that render the campaign themselves use this to
  // reach the same name {@link describeEventScope} appends, so the two never drift apart.
  public static getEventCouponName(trigger: TriggerCondition, couponNames?: Map<string, string>): string {
    if (trigger?.widgetId == null) {
      return '';
    }
    return (
      couponNames?.get(AutomationHelpers.buildEventScopeValue(trigger.widgetId, trigger.componentId ?? null)) ?? ''
    );
  }

  // Human-readable scope for a saved Event trigger, built from its typed fields. The coupon name comes
  // from a lookup the caller supplies; without it the campaign alone is all that can be shown, since a
  // component id means nothing to a reader.
  public static describeEventScope(
    trigger: TriggerCondition,
    widgets: WidgetSimpleInfo[],
    translateService: TranslateService,
    couponNames?: Map<string, string>,
  ): string {
    if (trigger?.widgetId == null) {
      return '';
    }
    const campaign = AutomationHelpers.getEventCampaignLabel(trigger.widgetId, widgets, translateService);
    const coupon = AutomationHelpers.getEventCouponName(trigger, couponNames);
    return coupon ? `${campaign} - ${coupon}` : campaign;
  }

  // A trigger is identified by type plus subType, so two Event triggers with different subtypes stay distinct
  public static triggerKey(trigger: TriggerCondition): string {
    return trigger?.subType ? `${trigger.type}:${trigger.subType}` : trigger?.type;
  }

  public static isSameTrigger(a: TriggerCondition, b: TriggerCondition): boolean {
    return AutomationHelpers.triggerKey(a) === AutomationHelpers.triggerKey(b);
  }

  public static isComponentActionTrigger(trigger: TriggerCondition): boolean {
    return trigger?.type === 'Goals' && AutomationHelpers.componentActionGoalRegex.test(trigger.argument ?? '');
  }

  private static getWidgetIdFromComponentActionTrigger(trigger: TriggerCondition): number | undefined {
    const match = trigger ? AutomationHelpers.componentActionGoalRegex.exec(trigger.argument ?? '') : null;
    return match ? +match[1] : undefined;
  }

  // A source type we don't have a case for still names itself; only a missing one falls back to the generic label.
  private static getComponentActionLabelKey(automation: Automation): string {
    const hasLabel = !!automation?.sourceLabel;
    switch (automation?.sourceType) {
      case AutomationSourceType.Button:
        return hasLabel ? 'automations.componentClickLabeled.Button' : 'automations.componentClick.Button';
      case AutomationSourceType.ButtonList:
        return hasLabel ? 'automations.componentClickLabeled.ButtonList' : 'automations.componentClick.ButtonList';
      case AutomationSourceType.State:
        return hasLabel ? 'automations.componentClickLabeled.State' : 'automations.componentClick.State';
      case AutomationSourceType.Image:
        return hasLabel ? 'automations.componentClickLabeled.Image' : 'automations.componentClick.Image';
      case AutomationSourceType.Text:
        return hasLabel ? 'automations.componentClickLabeled.Text' : 'automations.componentClick.Text';
      case AutomationSourceType.StepNavigation:
        return hasLabel
          ? 'automations.componentClickLabeled.StepNavigation'
          : 'automations.componentClick.StepNavigation';
      case undefined:
      case null:
        return 'automations.componentAction';
      default:
        return hasLabel ? 'automations.componentClickLabeledUnknown' : 'automations.componentClickUnknown';
    }
  }

  /** Label prefix only (ends with "in"/"i"); callers that render the campaign themselves use this. */
  public static getComponentActionLabel(translateService: TranslateService, automation: Automation): string {
    return translateService.instant(AutomationHelpers.getComponentActionLabelKey(automation), {
      label: automation?.sourceLabel,
      type: automation?.sourceType,
    });
  }

  /** Full "<label> <campaign> (<variant>)" text for summaries that render one plain/bold string. */
  public static getComponentActionDisplayName(
    translateService: TranslateService,
    widgets: Array<WidgetSimpleInfo>,
    automation: Automation,
    trigger: TriggerCondition,
    options?: { skipBoldText?: boolean; tooltip?: boolean },
  ): string {
    const widgetId = AutomationHelpers.getWidgetIdFromComponentActionTrigger(trigger);
    const relatedWidget = widgets?.find((widget) => widget.id === widgetId);
    if (!relatedWidget) {
      // Deleted widget: keep the existing removed-goal wording. An unnamed campaign is not a deleted one.
      return AutomationHelpers.prettifySingleGoalName(
        translateService,
        widgets ?? [],
        trigger.argument,
        options?.tooltip,
        options?.skipBoldText,
      );
    }
    const label = AutomationHelpers.getComponentActionLabel(translateService, automation);
    const widgetName = AutomationHelpers.getDynamicWidgetName(relatedWidget, translateService);
    return options?.skipBoldText ? `${label} ${widgetName}` : `${label} <b>${widgetName}</b>`;
  }

  public static prettifyGoalsNames(
    translateService: TranslateService,
    widgets: Array<WidgetSimpleInfo>,
    goals,
    propertyName,
  ) {
    if (goals) {
      goals.forEach((goal) => {
        const [oc, eventType, widgetId] = goal[propertyName].split('_');
        if (!oc || !eventType || !widgetId) return;
        if (widgetId && eventType) {
          const relatedWidget = widgets.find((widget: WidgetSimpleInfo) => +widgetId === widget.id);
          if (relatedWidget) {
            goal[propertyName] = this.getGoalNameFromString(relatedWidget, eventType, translateService);
          } else {
            switch (eventType) {
              case 'click':
                goal.displayName = translateService.instant('visits.goalEvents.clickThroughRemoved', {
                  widgetId: widgetId,
                });
                break;
              case 'submit':
                goal.displayName = translateService.instant('visits.goalEvents.formSubmissionRemoved', {
                  widgetId: widgetId,
                });
                break;
              case 'action':
                goal.displayName = translateService.instant('visits.goalEvents.componentActionRemoved', {
                  widgetId: widgetId,
                });
                break;
              default:
            }
          }
        }
      });
    }
    return goals.sort((a, b) => {
      const name1 = a.displayName?.toLowerCase();
      const name2 = b.displayName?.toLowerCase();
      return name1 > name2 ? 1 : -1;
    });
  }

  public static getDynamicWidgetName(relatedWidget: WidgetSimpleInfo, translateService: TranslateService): string {
    const campaignName = relatedWidget.campaignName
      ? relatedWidget.campaignName
      : translateService.instant('campaign.unnamed');
    if (relatedWidget.name) {
      return campaignName + ' (' + relatedWidget.name + ')';
    } else if (relatedWidget.campaignType !== OnsiteCampaignType.ABTesting) {
      const variantString = translateService.instant('campaign.widget.sidebar.form-settings.gen-automation-variant', {
        variantId: relatedWidget.order + 1,
      });
      return campaignName + ' ' + variantString;
    } else {
      const controlOrChallenger =
        relatedWidget.order === 0
          ? translateService.instant('campaign.ab-test.control')
          : translateService.instant('campaign.ab-test.challenger');
      const variantString = translateService.instant(
        'campaign.widget.sidebar.form-settings.gen-automation-variant-abtest',
        { abTestVariant: controlOrChallenger },
      );
      return campaignName + ' ' + variantString;
    }
  }

  private static getGoalNameFromString(
    relatedWidget: WidgetSimpleInfo,
    eventType: string,
    translateService: TranslateService,
  ): string {
    const widgetPrettyName = this.getDynamicWidgetName(relatedWidget, translateService);

    if (eventType === 'click') {
      return translateService.instant('visits.goalEvents.clickThrough') + ' <b>' + widgetPrettyName + '</b>';
    } else if (eventType === 'submit') {
      return translateService.instant('visits.goalEvents.formSubmission') + ' <b>' + widgetPrettyName + '</b>';
    } else if (eventType === 'discount') {
      return translateService.instant('visits.goalEvents.discount') + ' <b>' + widgetPrettyName + '</b>';
    } else if (eventType === 'action') {
      return translateService.instant('visits.goalEvents.componentAction') + ' <b>' + widgetPrettyName + '</b>';
    } else {
      return widgetPrettyName;
    }
  }

  public static prettifySingleGoalName(
    translateService: TranslateService,
    widgets: Array<WidgetSimpleInfo>,
    name,
    tooltip = false,
    skipBoldText = false,
  ) {
    if (name.startsWith('oc')) {
      const [oc, eventType, widgetId] = name.split('_');
      if (widgetId && eventType) {
        const relatedWidget = widgets.find((widget) => +widgetId === widget.id);
        if (relatedWidget) {
          name = this.getGoalNameFromString(relatedWidget, eventType, translateService);
        } else {
          switch (eventType) {
            case 'click':
              name = translateService.instant(
                tooltip ? 'visits.goalEvents.clickThroughRemovedTooltip' : 'visits.goalEvents.clickThroughRemoved',
                { widgetId: widgetId },
              );
              break;
            case 'submit':
              name = translateService.instant(
                tooltip ? 'visits.goalEvents.formSubmissionRemovedTooltip' : 'visits.goalEvents.formSubmissionRemoved',
                { widgetId: widgetId },
              );
              break;
            case 'discount':
              name = translateService.instant(
                tooltip ? 'visits.goalEvents.discountRemovedTooltip' : 'visits.goalEvents.discountRemoved',
                { widgetId: widgetId },
              );
              break;
            case 'action':
              name = translateService.instant(
                tooltip
                  ? 'visits.goalEvents.componentActionRemovedTooltip'
                  : 'visits.goalEvents.componentActionRemoved',
                { widgetId: widgetId },
              );
              break;
            default:
          }
        }
      }
    }
    if (skipBoldText) {
      name = name
        .replace(new RegExp('<b>', 'g'), '')
        .replace(new RegExp('</b>', 'g'), '')
        .replace(new RegExp('<strong>', 'g'), '')
        .replace(new RegExp('</strong>', 'g'), '');
    }
    return name;
  }

  public static prettifySingleWidgetName(
    widgets: Array<WidgetSimpleInfo>,
    widgetId: string,
    campaignNameFallback: string,
  ) {
    const widget = widgets.find((widget) => +widgetId === widget.id);
    if (widget) {
      return `${widget.campaignName || campaignNameFallback} (${widget.name})`;
    } else {
      return widgetId;
    }
  }

  public static excludePropListId(props: any[]): any[] {
    return props?.filter((prop) => prop.name !== PROPS_TYPE.ListId && prop.name !== PROPS_TYPE.TagIds);
  }

  public static pickPropListId(props: any[]): any {
    return props?.find((prop) => prop.name === PROPS_TYPE.ListId || prop.name === PROPS_TYPE.TagIds);
  }

  public static removeNoneProperties(data) {
    for (const actionGroup of data.actionsConfig.filter((x) => x.translationKey === 'field-mapping')) {
      for (const action of actionGroup.actionsGroup) {
        action.action.properties = action.action.properties.map((prop) => {
          if (prop.value === 'none') return { ...prop, value: '' };
          return prop;
        });
      }
    }
    return data;
  }

  public static setEmptyToNone(props: any[]): void {
    props.forEach((prop) => {
      if (typeof prop.value === 'string' && prop.value.trim() === '') {
        prop.value = 'none';
      }
    });
  }

  public static getDefaultConditionType(account: Account): string {
    let type = 'Email';
    if (account?.identifier) {
      switch (account.identifier) {
        case AccountIdentifierConstants.Telephone:
          type = 'Phone';
          break;
        case AccountIdentifierConstants.VoyadoContactId:
          type = 'WidgetFormSubmission';
          break;
      }
    }
    return type;
  }

  public static getNamePropForCreateAction(createRealTimeAction) {
    switch (createRealTimeAction.action.$type) {
      case ACTIONS_TYPES.VoyadoAddEmailToList:
        return PROPS_TYPE.Source;
      case ACTIONS_TYPES.DotdigitalCreateContact:
        return PROPS_TYPE.Lists;
      case ACTIONS_TYPES.RuleMailerAddTagToSubscriber:
        return PROPS_TYPE.TagIds;
      case ACTIONS_TYPES.RuleMailerAddTagToSubscriber:
        return PROPS_TYPE.TagIds;
      case ACTIONS_TYPES.CleverReachCreateContact:
        return null;
      case ACTIONS_TYPES.OmnisendCreateContact:
        return null;
      default:
        return PROPS_TYPE.ListIds;
    }
  }

  public static isBrevoDotVoyadoRuleRealTime(automationConfig) {
    return (
      automationConfig?.name === IntegrationType.Brevo ||
      automationConfig?.name === IntegrationType.CleverReach ||
      automationConfig?.name === IntegrationType.Omnisend ||
      automationConfig?.name === IntegrationType.Dotdigital ||
      automationConfig?.name === IntegrationType.Shopware ||
      ((automationConfig?.name === IntegrationType.Voyado || automationConfig?.name === IntegrationType.Rule) &&
        !!this.createActionOf(automationConfig)?.isRunningInRealTime)
    );
  }

  private static createActionOf(automationConfig) {
    return automationConfig?.actionsConfig?.find((group) => group.nameGroup === TypeStep.AddContact)?.actionsGroup?.[0]
      ?.action;
  }

  public static getIntegrationBasedOnAction(type) {
    //needs to be expanded with more Integrations are added
    let nameAction;
    switch (true) {
      case type.includes(IntegrationType.Rule):
        nameAction = IntegrationType.Rule;
        break;
      case type.includes(IntegrationType.Klaviyo):
        nameAction = IntegrationType.Klaviyo;
        break;
      case type.includes(IntegrationType.Custobar):
        nameAction = IntegrationType.Custobar;
        break;
      case type.includes(IntegrationType.Brevo):
        nameAction = IntegrationType.Brevo;
        break;
      case type.includes(IntegrationType.Dotdigital):
        nameAction = IntegrationType.Dotdigital;
        break;
      case type.includes(IntegrationType.Omnisend):
        nameAction = IntegrationType.Omnisend;
        break;
      case type.includes(IntegrationType.Voyado):
        nameAction = IntegrationType.Voyado;
        break;
    }
    return nameAction;
  }

  // Real time needs these mappings folded into the create action, and
  // isAllowFormMappingPropApplyInitial refuses to fold once they are persisted as separate actions.
  static hasPersistedFormMappedUpdateActions(automation, appName: string): boolean {
    const updateActions = automation?.actions?.filter(
      (action) => UpdateRealTimeActions.includes(action.$type) && action.$type.includes(appName),
    );
    return !!updateActions?.some((action) => action.fieldValue?.startsWith('{{session.'));
  }

  static setFieldsMappingActionProp(configuredApps) {
    configuredApps?.forEach((item) => {
      // Recompute both ways: a `fieldName` of '' or 'none' means the field is unmapped. Only ever
      // setting this true (the original bug) left the "Run in realtime" switch permanently disabled.
      const hasActiveFieldMapping = !!item.actionsConfig?.some(
        (actionConfig) =>
          actionConfig.nameGroup === TypeStep.FieldMapping &&
          actionConfig.actionsGroup?.some(
            (group) =>
              group.action &&
              ActionUpdateField.includes(group.action.$type as ACTIONS_TYPES) &&
              group.action.properties?.some(
                (prop) => prop.name === PROPS_TYPE.FieldName && prop.value && prop.value !== 'none',
              ),
          ),
      );
      item.actionsConfig?.forEach((actionConfig) => {
        actionConfig.actionsGroup?.forEach((group) => {
          if (group.action) {
            group.action.hasFieldsMappingAction = hasActiveFieldMapping;
          }
        });
      });
    });
  }
}
